import React, { useContext, useEffect, useRef, useState } from 'react';
import { Breadcrumb, Button, OverlayTrigger } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import { BiExit, BiListCheck, BiDevices, BiX } from "react-icons/bi";
import { BsEye, BsFileEarmarkPdfFill, BsFileEarmarkWordFill, BsPencil } from "react-icons/bs";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { EventContext, LoaderContext } from '../../../../App';
import HTMLEditor from '../../../../components/Editor/HTMLEditor';
import MessageModal from '../../../../components/MessageModal/MessageModal';
import { EventType, LS_SELECTED_MAT_ISSUE_YEAR, ReportStatus, currentYear, startYear } from '../../../../core/constants/common';
import MatAssessmentService from '../../../../core/service/MatAssessmentService';
import Utils from '../../../../core/utilities/Utils';
import assessment_pic from "../../../../images/Assessment.png";
import './MaterialityAssessmentReport.scss';
import MaterialityAssessmentSidebar from './MaterialityAssessmentSidebar';
import { isAiEnabledPage, isCoverPage, normalizeReportSections } from './materialityReportConfig';
import ReportService from '../../../../core/service/ReportService';
import ReportExportModal from '../../../SubscriberSupplierAdmin/Reporting/ReportExportModal';
import AIMaterialityTextGen from '../../../../components/AI/AIMaterialityTextGen';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import esg_ai from '../../../../images/esg_ai_Green.png';

/**
 * When periodEnd is missing or not a valid date, set it to the last calendar day
 * of the year derived from `period` (e.g. period 2026-01-01 → periodEnd 2026-12-31).
 * Mutates `reportData` in place.
 */
export const applyDefaultPeriodEndIfInvalid = (reportData) => {
	if (!reportData || typeof reportData !== 'object') {
		return reportData;
	}
	const periodEndInvalid =
		reportData.periodEnd == null ||
		reportData.periodEnd === '' ||
		!moment(reportData.periodEnd).isValid();
	if (!periodEndInvalid) {
		return reportData;
	}
	if (reportData.period && moment(reportData.period).isValid()) {
		reportData.periodEnd = moment(reportData.period).endOf('year').format('YYYY-MM-DD');
	}
	return reportData;
};

const MaterialityAssessmentReport = ({ t }) => {

	// All hooks must be called first, in the same order every render
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const [reportId, setReportId] = useState(searchParams.get('reportId') ?? 0);
	const [report, setReport] = useState({});
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [activePageIndex, setActivePageIndex] = useState(0);
	const [activeSectionIndex, setActiveSectionIndex] = useState(0);
	const [edited, setEdited] = useState(false);
	const editorRef = useRef(null);
	const [contentToEdit, setContentToEdit] = useState({});
	const [contentToEditBackup, setContentToEditBackup] = useState({});
	const [saveEnabled, setSaveEnabled] = useState(false);
	const [filter, setFilter] = useState(() => {
		const selectedYear = localStorage.getItem(LS_SELECTED_MAT_ISSUE_YEAR) ?? null;
		const year = new Date().getFullYear();
		return {
			year: selectedYear ?? `${year}`,
		};
	});
	const [showMessageModal, setShowMessageModal] = useState(false);
	const [modalMessage, setModalMessage] = useState("There is no active report for the period selected");
	const [showReport, setShowReport] = useState(false);
	const [pageCount, setPageCount] = useState(0);
	const { setEvent } = useContext(EventContext);
	const [reportToExport, setReportToExport] = useState(false);
	const [reportName, setReportName] = useState('');
	const [portrait, setPortrait] = useState(true);
	const [showFullView, setShowFullView] = useState(true);
	const [showAITextGen, setShowAITextGen] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	
	// Non-hook variables after all hooks
	const selectedYear = localStorage.getItem(LS_SELECTED_MAT_ISSUE_YEAR) ?? null;
	const year = new Date().getFullYear();
	const yearOptions = [];
	for (let i = currentYear; i >= startYear; i--) {
		yearOptions.push(<option value={i} key={i}>{i}</option>);
	}

	useEffect(() => {
		localStorage.setItem(LS_SELECTED_MAT_ISSUE_YEAR, filter.year);
		if (reportId > 0) {
			fetchReportFromId(reportId, 'initial');
		} else {
			fetchReport('initial');
		}
	}, [filter]);

	useEffect(() => {
		setShowAITextGen(false);
		if (isAiEnabledPage(contentToEdit?.name)) {
			// Keep split view available for AI-assisted editing
		} else {
			setShowFullView(true);
		}
	}, [contentToEdit]);

	const gotoList = () => {
		navigate("/company_admin/dmat/report/list");
	}

	const removeSearchParams = () => {
		window.history.pushState({}, '', window.location.pathname);
	};

	const fetchReport = (type) => {
		setLoaderDisplay(true);
		MatAssessmentService.getLatestReport(filter.year).then(res => {
			setLoaderDisplay(false);
			if (res.status === 200) {
				if (res.data.status != 'Failed') {
					setShowReport(true);
					setReportId(res.data.id);
					setReportName(res.data.name);
					const reportClone = res.data;
					reportClone.sections?.sort((a, b) => {
						return a.sortOrder - b.sortOrder;
					});
					reportClone.sections?.forEach((section) => {
						section.pages?.sort((a, b) => a.sortOrder - b.sortOrder);
						section.pages?.forEach((page) => {
							const decodedHTML = page.htmlContent
								? decodeURIComponent(escape(window.atob(page.htmlContent)))
								: null;
							page.html = decodedHTML ?? null;
						});
					});
					const normalizedReport = normalizeReportSections(reportClone);
					if (type === 'initial' && normalizedReport.sections?.[0]?.pages?.[0]) {
						const firstPage = normalizedReport.sections[0].pages[0];
						setContentToEdit(firstPage);
						setContentToEditBackup(firstPage);
						setActiveSectionIndex(0);
						setActivePageIndex(0);
					}
					setReport(normalizedReport);
					setLoaderDisplay(false);
				} else {
					setShowReport(false);
					setReportId(0);
					setReportName('');
				}
			}
		}).catch(error => {
			setLoaderDisplay(false);
			setShowReport(false);
			setReportId(0);
			setReportName('');
			toast.error(error.title, {
				position: toast.POSITION.TOP_RIGHT,
			});
		});

	};

	const fetchReportFromId = (id, type) => {
		setLoaderDisplay(true);
		MatAssessmentService.getReport(id).then(res => {
			setLoaderDisplay(false);
			if (res.status === 200) {
				if (res.data.status != 'Failed') {
					setShowReport(true);
					setReportId(res.data.id);
					setReportName(res.data.name);
					const reportClone = res.data;
					reportClone.sections?.sort((a, b) => {
						return a.sortOrder - b.sortOrder;
					});
					reportClone.sections?.forEach((section) => {
						section.pages?.sort((a, b) => a.sortOrder - b.sortOrder);
						section.pages?.forEach((page) => {
							const decodedHTML = page.htmlContent
								? decodeURIComponent(escape(window.atob(page.htmlContent)))
								: null;
							page.html = decodedHTML ?? null;
						});
					});
					const normalizedReport = normalizeReportSections(reportClone);
					if (type === 'initial' && normalizedReport.sections?.[0]?.pages?.[0]) {
						const firstPage = normalizedReport.sections[0].pages[0];
						setContentToEdit(firstPage);
						setContentToEditBackup(firstPage);
						setActiveSectionIndex(0);
						setActivePageIndex(0);
					}
					setReport(normalizedReport);
					setLoaderDisplay(false);
				} else {
					setShowReport(false);
					setReportId(0);
					setReportName('');
				}
			}
		}).catch(error => {
			setLoaderDisplay(false);
			toast.error(error.title, {
				position: toast.POSITION.TOP_RIGHT,
			});
		});

	};

	const onCardClick = () => {
		setEdited(true);
	};

	const saveReport = (htmlData) => {
		const updatedReport = { ...report };
		updatedReport.sections[activeSectionIndex].pages[activePageIndex].html = htmlData;
		const prevData = { ...contentToEdit };
		prevData.html = htmlData;
		setContentToEdit(prevData);
		setContentToEditBackup(prevData);
		setReport(updatedReport);
		updateReport();
	};

	const updateReport = () => {
		const reportData = JSON.parse(JSON.stringify(report));
		applyDefaultPeriodEndIfInvalid(reportData);
		reportData.status = ReportStatus.DRAFT;
		reportData.sections?.forEach(section => {
			section?.pages?.forEach((page, index) => {
				let trimmedHTML = page.html?.replace(/>\s+</g, '><');
				page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
				delete page.html;
				delete page.json;
				delete page.index;
				delete page.jsonContent;
				delete page.pageNum;
			});
			delete section.index;
		});
		MatAssessmentService.saveReport(reportData.id, reportData).then(res => {
			if (res.status === 200) {
				toast.success("Report updated successfully", {
					position: toast.POSITION.TOP_RIGHT,
				});
				setReportId(res.data.id);
				// fetchReport('reload');
				fetchReportFromId(res.data.id, 'reload');
			}
		}).catch(error => {
			toast.error(error.title, {
				position: toast.POSITION.TOP_RIGHT,
			});
		});
	};

	const callbackMessageModal = (boolVal) => {
		setShowMessageModal(false);
		gotoList()
	};

	const blankCardToRender = () => {
		return (
			<div className="Assessment_blankcard">
				<div>
					<div className="assessment_pic">
						<img src={assessment_pic} alt="Assessment" />
					</div>
					<h4 className="mb-4">No reports generated for the selected year</h4>
					<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("ReportList.label"))}>
						<Button variant='primary' onClick={() => gotoList()}><BiListCheck /> Goto list</Button>
					</OverlayTrigger>
				</div>
			</div>
		);
	};

	const generatePDF = () => {
		setLoaderDisplay(true);
		ReportService.generateESRSPDF(reportId)
			.then((res) => {
				setLoaderDisplay(false);
				let blob = new Blob([res.data], { type: 'application/pdf' });
				// Creating new object of PDF file
				const fileURL = window.URL.createObjectURL(blob);
				let link = document.createElement('a');
				link.href = fileURL;
				link.download = `${reportName}.pdf`;
				link.click();
			})
			.catch((error) => {
				setLoaderDisplay(false);
				toast.error(error.title, {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
	};

	const previewReport = () => {
		setLoaderDisplay(true);
		ReportService.generateESRSPDF(reportId)
			.then((res) => {
				setLoaderDisplay(false);
				let blob = new Blob([res.data], { type: 'application/pdf' });
				// Creating new object of PDF file
				const fileURL = window.URL.createObjectURL(blob);
				let link = document.createElement('a');
				link.href = fileURL;
				link.target = '_blank';
				link.click();
			})
			.catch((error) => {
				setLoaderDisplay(false);
				toast.error(error.title, {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
	};


	const renderAIChat = () => {
		return (
			<div className="Report_ESGAICopilot">
				<div className='p-0'>
					<AIMaterialityTextGen
						onHide={() => { setShowAITextGen(false); showFullView(true) }}
						onTransferToEditor={(text) => transferTextToEditor(text)}
						content={contentToEdit} onClearResult={showAITextGen} page={activePageIndex} section={activeSectionIndex} />
				</div>
			</div>
		);
	}

	const transferTextToEditor = (text) => {
		setEdited(true);
		setSaveEnabled(true);
		const c = JSON.parse(JSON.stringify(contentToEdit));
		c.html = text;
		setContentToEdit(c);
	};

	const onCancelEdited = () => {
		if (saveEnabled) {
			setShowConfirmation(true);
		} else {
			setEdited(false);
			setSaveEnabled(false);
		}
	}

	const confirmCancel = (val) => {
		setShowConfirmation(false);
		if (val) {
			console.log(contentToEditBackup);
			setContentToEdit(prevState => ({
				...prevState,
				html: contentToEditBackup.html
			}));
			setEdited(false);
			setSaveEnabled(false);
			setContentToEditBackup({})
		}
	};

	return (
		<div className='MaterialityAssessmentReport'>
			{!showReport && blankCardToRender()}
			{showReport &&
				<>
					<MaterialityAssessmentSidebar
						sections={report?.sections}
						activeSectionIndex={activeSectionIndex}
						activePageIndex={activePageIndex}
						onPageChange={(sIndex, pIndex) => {
							setActiveSectionIndex(sIndex);
							setActivePageIndex(pIndex);
							setContentToEdit(report.sections[sIndex].pages[pIndex]);
							setContentToEditBackup(report.sections[sIndex].pages[pIndex]);
							setEdited(false);
						}}
					/>

					<div className='MAR_card'>
						<div className='floating-actionSticky px-1 pb-2'>
							<div className='floating-action-panel row'>
								<div className='col-lg-5'>
									<Breadcrumb>
										<Breadcrumb.Item onClick={() => navigate(`/company_admin/dmat/stats`)}>{t("Materiality.label")}</Breadcrumb.Item>
										<Breadcrumb.Item active>{report?.name}</Breadcrumb.Item>
									</Breadcrumb>
								</div>
								<div className='action-holder RS_btn_head col-md-7'>

									{edited &&
										<button className='btn btn-outline-primary px-3'
											disabled={!saveEnabled}
											onClick={() => {
												saveReport(editorRef?.current?.getContent());
												setEdited(false);
												setSaveEnabled(false);
											}}>
											{t("SaveDraft.label")}
										</button>
									}
									{edited &&
										<button className='btn btn-secondary px-4'
											onClick={() => {
												onCancelEdited()
											}}>
											{t("Cancel.label")}
										</button>
									}
									{!edited &&
										<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Edit.label"))}>
											<button className='btn btn-light' onClick={() => setEdited(true)}>
												<BsPencil /></button>
										</OverlayTrigger>
									}
									<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Preview.label"))}>
										<button className='btn btn-light' onClick={() => previewReport()}> <BsEye /></button>
									</OverlayTrigger>
									<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("DownloadAsPDF.label"))}>
										<button className='btn btn-light' onClick={() => generatePDF()}><BsFileEarmarkPdfFill className='pdf' /></button>
									</OverlayTrigger>
									<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("DownloadAsWord.label"))}>
										<button className='btn btn-light' onClick={() => setReportToExport(true)}><BsFileEarmarkWordFill className='word' /></button>
									</OverlayTrigger>
									<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Portrait.label"))}>
										<Button variant='light' onClick={() => setPortrait(!portrait)}><BiDevices /> </Button>
									</OverlayTrigger>
									{showFullView && isAiEnabledPage(contentToEdit?.name) &&
										<button className='btn btn-ESGAi me-2 px-3'
											onClick={() => {
												setShowFullView(false);
												setShowAITextGen(true);
											}}>{t("ESGAIMateriality.label")}
										</button>
									}
									<select
										className="form-select  w-auto"
										value={filter.year}
										onChange={(e) => {
											removeSearchParams();
											setReportId(0);
											setFilter({ ...filter, year: e.target.value });
											setEvent({
												event: EventType.DOUBLE_MAT_YEAR_CHANGED,
												data: { year: e.target.value },
											});
										}}>
										{yearOptions}
									</select>
									<Button variant='primary' className='btn' onClick={() => gotoList()}><BiExit /> Exit</Button>
								</div>
							</div>
						</div>
						<div className='row m-0'>
							<div className={showFullView ? 'col-md-12 ps-2 pe-0' : 'col-md-8 ps-2 pe-0'}>
								<section
									className={`MAR_cardSection ${isCoverPage(contentToEdit?.name) ? ' cover' : ' contents'} ${portrait ? 'portrait' : ''}`}
									onClick={() => onCardClick()}
								>
									<div className={isCoverPage(contentToEdit?.name) ? 'cover_dt' : 'MAR_cardSection_inner'}>
										{
											edited
												? <HTMLEditor editorRef={editorRef} initialValue={contentToEdit?.html}
													onContentChange={(e) => {
														setSaveEnabled(true);
													}} />
												: <div dangerouslySetInnerHTML={{ __html: contentToEdit?.html }}>
												</div>
										}
									</div>
									{activeSectionIndex > 1 && contentToEdit?.pageNum != null && <div className='page-number'>{reportName} | {contentToEdit.pageNum}</div>}
								</section>
							</div>

							<div className={showFullView ? 'd-none' : 'col-md-4 ps-2 pe-0'}>
								<div className='RightSide_Tab 3'>
									<div className='RightSide_Tab_head'>
										<div className='RightSide_Tab_head_in'>
											<span className='widget-header_inner'><img src={esg_ai} width={15} height={15} /> {t('ESGAICopilot.label')}</span>
											<span className='cursor' onClick={() => { setShowFullView(true) }}>
												<BiX />
											</span>
										</div>
									</div>
									<div>
										{
											renderAIChat()
										}
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			}

			{showMessageModal && (
				<MessageModal
					modalShow={true}
					messageText={modalMessage}
					callbackModal={callbackMessageModal}
					id=""
					title={'Materiality Report'}
				/>
			)}

			{
				reportToExport &&
				<ReportExportModal
					reportId={reportId}
					exportAs={'WORD'}
					onHide={() => setReportToExport(false)}
					activeView={'DOUBLE_MAT'}
				/>
			}

			{
				showConfirmation &&
				<ConfirmationModal
					type={'Double_Materiality'}
					modalShow={true}
					messageText={t("LeaveEditorPrompt.label")}
					callbackModal={confirmCancel}
					confirmBtn={t("Confirm.label")}
					CancelBtn={t("Cancel.label")}
				/>
			}

		</div>
	);
};

export default withTranslation()(MaterialityAssessmentReport);